import { isAfter, startOfDay } from "date-fns";

import type { CustomerCartItem, CustomerCartItems, SessionContext } from "../../model/CustomerCartTypes";
import type { CommonCart, OrderData } from "../../model/CommonCartTypes";
import type { AddItemParams, ICartStore } from "../common-cart/CommonCartStore";
import { generateRequestId } from "../common-cart/CommonCartStore";
import type { EmployeeOrderHeader, OrderSource } from "../CheckoutTypes";
import type { AnonCart } from "../../model/AnonCartTypes";
import type { CartItemChange } from "../MiniCartStore";
import type { ArticleData } from "../../model/ArticleTypes";
import { createDummyArticle, extractArticleData } from "../../model/ArticleTypes";
import { getArticleDataBatched } from "../../server/ArticleServerRequests";
import type { ArticlePrices } from "../../model/Prices";
import type { PaymentType } from "../../model/CheckoutData";
import { CustomerCartWebsocketConnector } from "../common-cart/CartWebsocketConnector";
import { CustomerCartEventRepository } from "../common-cart/CartEventRepository";
import { Store } from "../../utils/Store";
import { EventSourcedCartStoreImpl } from "./EventSourcedCartStore";
import { SessionStore } from "../../uidispatcherapi/SessionStore";
import { getQuantityForPopup } from "../MiniCartUtil";
import { AnonCartStoreImpl } from "../anon-cart/AnonCartStore";
import { AnonCartActions } from "../../actions/AnonCartActions";
import * as CustomerCartServerRequests from "../../server/CustomerCartServerRequests";
import { CustomerCartActions } from "../../actions/CustomerCartActions";
import { EmployeeOrderHeaderActions } from "../../actions/EmployeeOrderHeaderActions";
import { SequentialRequestProcessor } from "../../server/SequentialRequestProcessor";
import { CheckoutAggregatorStore, CheckoutAggregatorStoreImpl } from "../checkout/CheckoutAggregatorStore";
import { CustomerDataStore } from "../CustomerDataStore";
import { CustomerCartContextStore } from "../common-cart/CartContextStore";
import { SavedCartsStore } from "./UnifiedCartSearchStore";
import {
	CartDeletedPayload,
	CartRestockedPayload,
	CartSavedPayload,
	CheckedOutPayload,
	CustomerCartEvent,
	DeliveryDateSetPayload,
	ItemAddedPayload,
	ItemBatchAddedPayload,
	ItemCommentSetPayload,
	ItemQuantitySetPayload,
	ItemRemovedPayload,
	ItemServiceOptionsSetPayload,
	OrderHeaderChangedPayload,
	SavedCartLoadedPayload,
	SetManualPricePayload,
} from "./CustomerCartEvents";
import { setServiceOption } from "../../model/ServiceOptions";
import { floorToThreeDecimalPlaces, minQuantity } from "../../utils/NumberUtil";
import { SavedCartActions } from "../../actions/SavedCartActions";
import { TrackingStoreGAUtils } from "../TrackingStoreGAUtils";
import { DeliveryDatesStore } from "../DatesProvider";
import { AnonymousCartMetaStore } from "../../uidispatcherapi/AnonymousCartMetaStore";
import { CustomerCartMetaStore } from "../../uidispatcherapi/CustomerCartMetaStore";
import { formatLocalDate, parseDate } from "../../utils/DateUtil";
import { CommonStatus, emitChangeItemQuantityCommandEvent } from "../../events/CartEvents";
import { isFsdDeliveryDateBeforeCheckoutEnabled } from "../../ABFeatures";

export const DELIVERY_DATE_FORMAT = "yyyy-MM-ddxxx";
export const isAfterOrderHeaderDeliveryDate = (
	cartItemDeliveryDate: string | null | undefined,
	orderHeaderDeliveryDate: string | null | undefined,
): boolean => {
	if (!cartItemDeliveryDate || !orderHeaderDeliveryDate) {
		return false;
	}

	const parsedDeliveryDate = parseDate(orderHeaderDeliveryDate, DELIVERY_DATE_FORMAT);
	const parsedItemDeliveryDate = parseDate(cartItemDeliveryDate);
	return isAfter(startOfDay(parsedItemDeliveryDate), startOfDay(parsedDeliveryDate));
};
export class CustomerCartStoreImpl extends Store implements ICartStore {
	_eventSourcedCartStore: EventSourcedCartStoreImpl;
	_articles: Map<string, ArticleData>;
	_optimisticArticles: Map<string, ArticleData>;
	_checkoutAggregatorStore: CheckoutAggregatorStoreImpl;
	_savedCartsStore: typeof SavedCartsStore;
	_deliveryDatesStore: typeof DeliveryDatesStore;
	_state: {
		cartIdPromise: Promise<string | null> | null | undefined;
	};

	constructor(
		eventSourcedCartStore: EventSourcedCartStoreImpl,
		checkoutAggregatorStore: CheckoutAggregatorStoreImpl,
		savedCartsStore: typeof SavedCartsStore,
		deliveryDatesStore: typeof DeliveryDatesStore,
	) {
		super();
		this._eventSourcedCartStore = eventSourcedCartStore;
		this._checkoutAggregatorStore = checkoutAggregatorStore;
		this._savedCartsStore = savedCartsStore;
		this._deliveryDatesStore = deliveryDatesStore;
		this._state = {
			cartIdPromise: null,
		};
		this._articles = new Map();
		this._optimisticArticles = new Map();
		this.listenTo(this._eventSourcedCartStore, () => {
			this.onCartStoreChange().catch((error) => {
				console.error("onCartStoreChange in CustomerCartStore failed with ", error);
			});
		});
		//CustomerCartStore needs the LazyStore SavedCartsStore to be there when it dispatches the SavedCartActions.addSavedCartOptimistically
		this.listenTo(this._savedCartsStore, () => {});
		this.listenTo(CustomerCartActions.addItem, (...args) => {
			// @ts-expect-error
			this.addItem(...args).catch((error) => {
				console.error("addItem in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(CustomerCartActions.setItemQuantity, (bundleId, quantity) => {
			this.setItemQuantity(bundleId, quantity).catch((error) => {
				console.error("setItemQuantity in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(CustomerCartActions.setItemComment, (...args) => {
			// @ts-expect-error
			this.setItemComment(...args).catch((error) => {
				console.error("setItemComment in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(CustomerCartActions.saveCart, (...args) => {
			// @ts-expect-error
			this.saveCart(...args).catch((error) => {
				console.error("saveCart in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(CustomerCartActions.deleteItem, (bundleId) => {
			this.removeItem(bundleId).catch((error) => {
				console.error("removeItem in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(CustomerCartActions.deleteItems, (bundleIds) => {
			this.removeItems(bundleIds).catch((error) => {
				console.error("removeItems in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(CustomerCartActions.deleteCart, () => {
			this.deleteCart().catch((error) => {
				console.error("deleteCart in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(CustomerCartActions.onSetManualPrice, (...args) => {
			// @ts-expect-error
			this.setManualPrice(...args).catch((error) => {
				console.error("setManualPrice in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(CustomerCartActions.setItemServiceOption, (...args) => {
			// @ts-expect-error
			this.setItemServiceOption(...args).catch((error) => {
				console.error("setItemServiceOption in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(CustomerCartActions.addItems, (...args) => {
			// @ts-expect-error
			this.addItems(...args).catch((error) => {
				console.error("addItems in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(CustomerCartActions.onRestockCart, (restockCartId: string) => {
			this.restockCart(restockCartId).catch((error) => {
				console.error("restockCart in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(EmployeeOrderHeaderActions.setSendOrderConfirmationToCustomer, (newValue) => {
			this.setOrderOptions({
				sendConfirmationToCustomer: newValue,
			}).catch((error) => {
				console.error("setOrderOptions in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(EmployeeOrderHeaderActions.setSendOrderConfirmationToCardholder, (newValue) => {
			this.setOrderOptions({
				sendConfirmationToCardholder: newValue,
			}).catch((error) => {
				console.error("setOrderOptions in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(EmployeeOrderHeaderActions.setSendPickingListToCentralWarehouse, (newValue) => {
			this.setOrderOptions({
				externalCommissioning: newValue,
			}).catch((error) => {
				console.error("setOrderOptions in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(EmployeeOrderHeaderActions.setIgnoreFees, (newValue) => {
			this.setOrderOptions({
				ignoreMinOrderValue: newValue,
			}).catch((error) => {
				console.error("setOrderOptions in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(EmployeeOrderHeaderActions.setSingleOrder, (newValue) => {
			this.setOrderOptions({
				singleOrder: newValue,
			}).catch((error) => {
				console.error("setOrderOptions in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(EmployeeOrderHeaderActions.setEgalisationTaxFlag, (newValue) => {
			this.setOrderOptions({
				applyEgalisationTax: newValue,
			}).catch((error) => {
				console.error("setOrderOptions in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(EmployeeOrderHeaderActions.setPayment, (paymentType: PaymentType) => {
			this.setPaymentOptions(paymentType).catch((error) => {
				console.error("setPaymentOptions in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(EmployeeOrderHeaderActions.setOrderSource, (orderSource) => {
			this.setOrderSource(orderSource).catch((error) => {
				console.error("setOrderSource in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(EmployeeOrderHeaderActions.setShoppingCartComment, (generalComment) => {
			this.setOrderComments({
				general: generalComment,
			}).catch((error) => {
				console.error("setOrderComments in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(EmployeeOrderHeaderActions.setCustomerDataAdditions, (additions) => {
			this.setOrderComments({
				picking: additions.pickingComment,
				invoice: additions.invoiceComment,
				delivery: additions.deliveryComment,
			}).catch((error) => {
				console.error("setOrderComments in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(EmployeeOrderHeaderActions.setOwnOrderNumber, (ownOrderNumber) => {
			this.setOwnOrderNumber(ownOrderNumber).catch((error) => {
				console.error("setOwnOrderNumber in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(EmployeeOrderHeaderActions.setOwnStoreNumber, (ownStoreNumber) => {
			this.setOwnStoreNumber(ownStoreNumber).catch((error) => {
				console.error("setOwnStoreNumber in CustomerCartStore failed with ", error);
			});
		});
		this.listenTo(CustomerCartActions.onLoadSavedCart, (savedCartId: string) => {
			this.loadSavedCart(savedCartId).catch((error) => {
				console.error("loadSavedCart in CustomerCartStore failed with ", error);
			});
		});
	}

	updateCartIdInMetaStore = (cartId: string | null | undefined): void => {
		//meta store checks on its own whether this is a new value and only updates if it is
		CustomerCartMetaStore.setCartId(cartId);
	};

	onCartStoreChange = async (): Promise<void> => {
		const targetCartId = this._eventSourcedCartStore.getCartId();
		this.updateCartIdInMetaStore(targetCartId);

		if (targetCartId) {
			this.updateArticleData().catch((error) => {
				console.error("updateArticleData in CustomerCartStore failed with ", error);
			});

			this.trigger();
			return;
		}

		const sessionContext = this._eventSourcedCartStore.getSessionContext();

		if (!sessionContext) {
			this.trigger();
			return;
		}

		const cartId = await this.getActiveCartId(sessionContext);
		this.updateCartIdInMetaStore(cartId);

		if (!cartId) {
			return;
		}

		// This has to happen, before we merge the anon cart.
		this._eventSourcedCartStore.setCartId(cartId);

		const anonCartId = AnonymousCartMetaStore.getCartId();
		const anonStoreId = AnonymousCartMetaStore.getStoreId();
		if (anonCartId && anonStoreId == sessionContext.storeId && !SessionStore.isEmployee()) {
			await this.handleAnonCart(anonCartId, sessionContext, cartId);
		}

		this.trigger();
	};

	updateArticleData = async (): Promise<void> => {
		const { items } = this._eventSourcedCartStore.getOptimisticState() || {};

		if (!items) {
			return;
		}

		const freeProductBundleIds = Object.values(items)
			.map((item: CustomerCartItem) => item.prices?.freeProducts)
			.flatMap((freeProducts) => freeProducts?.map((freeProduct) => freeProduct.bundleId))
			.filter((bundleId): bundleId is string => !!bundleId);

		const allBundleIds: string[] = [...Object.keys(items), ...freeProductBundleIds];
		const articlesInCache: string[] = [...this._articles.keys()];
		const itemsHaveChanged: boolean =
			allBundleIds.length !== articlesInCache.length ||
			!!allBundleIds.find((bundleId) => !articlesInCache.includes(bundleId));

		if (!itemsHaveChanged) {
			return;
		}

		const articles: Map<string, ArticleData> = this.getExistingArticles(items, this._articles);
		const missingBundles = allBundleIds.filter((bundleId) => !articles.has(bundleId));

		if (!missingBundles.length) {
			this._articles = articles;
			return;
		}

		const sessionContext = this._eventSourcedCartStore.getSessionContext();

		if (!sessionContext) {
			return;
		}

		const dummyData = missingBundles.map((bundleId) => {
			const optimisticArticleData = this._optimisticArticles.get(bundleId);

			this._optimisticArticles.delete(bundleId);

			return [bundleId, optimisticArticleData || createDummyArticle(bundleId)];
		});
		// @ts-expect-error
		this._articles = new Map([...articles.entries(), ...dummyData]);
		// We don't trigger here, because the next line will suspend and return to the caller.
		// It's up to the caller to trigger now or await this function for the full data.
		const remoteData = await getArticleDataBatched(sessionContext, missingBundles);
		this._articles = new Map([...this._articles.entries(), ...remoteData.entries()]);
		this.trigger();
	};
	getExistingArticles = (
		items: CustomerCartItems,
		articlesCache: Map<string, ArticleData>,
	): Map<string, ArticleData> => {
		return Object.entries(items).reduce((articles, [bundleId, item]) => {
			const articleData: ArticleData | null | undefined = extractArticleData(item) || articlesCache.get(bundleId);

			if (articleData) {
				articles.set(bundleId, articleData);
			}

			return articles;
		}, new Map());
	};
	assignArticleData = (items: CustomerCartItems, articles: Map<string, ArticleData>): CustomerCartItems => {
		return Object.entries(items).reduce((result, [key, item]) => {
			const articleData = articles.get(item.bundleId) ?? { serviceOptions: undefined };
			const serviceOptions = (articleData.serviceOptions ?? []).map((option) => ({
				...option,
				selected: item.selectedServiceOptions?.includes(option.id),
			}));
			const freeProducts = (item.prices?.freeProducts ?? []).map((freeProduct) => {
				if (!freeProduct.bundleId) {
					return freeProduct;
				}

				const freeProductArticleData: ArticleData | null | undefined = articles.get(freeProduct.bundleId);
				return freeProductArticleData ? { ...freeProduct, articleData: freeProductArticleData } : freeProduct;
			});
			const prices: ArticlePrices | null | undefined = freeProducts.length
				? ({ ...item.prices, freeProducts } as ArticlePrices)
				: item.prices;
			result[key] = { ...item, ...articleData, serviceOptions, prices };
			return result;
		}, {});
	};

	getCart(): CommonCart | null {
		const cart = this._eventSourcedCartStore.getOptimisticState();

		if (!cart?.context) {
			return null;
		}

		const { country, storeId, cardholderNumber, fsdAddressId } = cart.context;
		const {
			cartId,
			cartVersion,
			customerId,
			fsdDeliveryDate,
			totalPrices,
			items: rawItems,
			movPerSupplierInfo,
		} = cart;
		const items = this.assignArticleData(rawItems, this._articles);
		return {
			cartId,
			cartVersion,
			country,
			storeId,
			fsdAddressId,
			fsdDeliveryDate,
			customerId,
			// @ts-expect-error
			items,
			movPerSupplierInfo,
			cardholderNumber,
			totalPrices,
		};
	}

	getOrderData() {
		const cart = this._eventSourcedCartStore.getOptimisticState();

		return cart && cart.orderData;
	}

	getDeliveryDate() {
		const cart = this._eventSourcedCartStore.getOptimisticState();
		const fsdDeliveryDate = cart?.fsdDeliveryDate;
		let deliveryDate = cart?.orderData.deliveryDate;
		if (isFsdDeliveryDateBeforeCheckoutEnabled() && fsdDeliveryDate) {
			deliveryDate = fsdDeliveryDate;
		}
		return deliveryDate;
	}

	isPending() {
		return this._eventSourcedCartStore.isPending();
	}

	isEmployeeCart = () => {
		const cart = this._eventSourcedCartStore.getOptimisticState();

		return (cart && cart.context && cart.context.isEmployeeCart) || false;
	};
	_event2ItemChange = (event: CustomerCartEvent | null | undefined): CartItemChange | null | undefined => {
		const cart = this.getCart();

		if (!event || !cart) {
			return null;
		}

		const item = cart.items[event.payload.data.bundleId];

		if (!item) {
			return null;
		}

		const quantity = event.payload.data.quantity;
		const weightPerPiece = item.weightPerPiece;
		return {
			eventId: event.eventId,
			articleDescription: item.description,
			newQuantity: getQuantityForPopup(quantity, weightPerPiece),
		};
	};
	itemChangeEventTypes: Set<string> = new Set(["ItemQuantitySetPayload", "ItemAddedPayload"]);
	isCartItemChange = (event: CustomerCartEvent) => this.itemChangeEventTypes.has(event.payload.type);

	getCartItemChange(): CartItemChange | null | undefined {
		const itemChangeEvents = this._eventSourcedCartStore.getAppliedEvents().filter(this.isCartItemChange);

		return this._event2ItemChange(itemChangeEvents[itemChangeEvents.length - 1]);
	}

	async handleAnonCart(anonCartId: string, sessionContext: SessionContext, targetCartId: string) {
		// fetching an explicit anon cart makes sure, we will not merge a cart from
		// a wrong store into the active customer cart.
		const anonCart = await AnonCartStoreImpl.getExplicitAnonCart(anonCartId, {
			country: sessionContext.country,
			storeId: sessionContext.storeId,
			locale: sessionContext.locale,
			anonUserId: SessionStore.getAnonymousUserId(),
		});

		if (anonCart) {
			await this._mergeAnonCart(anonCart, targetCartId);
		}
	}

	async getActiveCartId(sessionContext: SessionContext) {
		const currentCart = await this._eventSourcedCartStore.execute(() =>
			CustomerCartServerRequests.getCurrentCart({
				sessionContext,
			}),
		);
		const cartId = currentCart && currentCart.cartId;
		if (cartId) return cartId;

		if (this._state.cartIdPromise) {
			return this._state.cartIdPromise;
		}

		this._state = {
			cartIdPromise: this.createCart(sessionContext),
		};
		const newCartId = await this._state.cartIdPromise;
		this._state = {
			cartIdPromise: null,
		};
		return newCartId;
	}

	async createCart(sessionContext: SessionContext): Promise<string | null> {
		const requestId = generateRequestId();
		return this._eventSourcedCartStore.execute(() => {
			const response = CustomerCartServerRequests.postCart({
				sessionContext,
				requestId,
			});
			return response;
		});
	}

	async deleteCart() {
		const payload = new CartDeletedPayload();
		this._eventSourcedCartStore.send(
			payload,
			(sessionContext, event) => {
				const response = CustomerCartServerRequests.deleteCart({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
				});
				return response;
			},
			true,
		);
	}

	async addItem({ quantity, bundleId, comment, articleData }: AddItemParams, eventIdPrefix?: string) {
		if (quantity < minQuantity) {
			return null;
		}

		if (articleData) {
			this._optimisticArticles.set(bundleId, articleData);
		}

		const scaledQuantity = floorToThreeDecimalPlaces(quantity);
		return this._eventSourcedCartStore.send(
			new ItemAddedPayload({
				bundleId,
				quantity: scaledQuantity,
				comment,
				addedTimestamp: new Date().getTime(),
				addedBy: CustomerCartStoreImpl.getUserId(),
			}),
			(sessionContext, event) => {
				const response = CustomerCartServerRequests.postItem({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
					bundleId,
					quantity: scaledQuantity,
					comment,
				});
				emitChangeItemQuantityCommandEvent({
					cartId: event.cartId,
					commonStatus: "ACTIVE" as CommonStatus, // can only be active
					requestId: event.eventId,
					bundleId,
					previousQuantity: 0,
					newQuantity: quantity,
				});
				return response;
			},
			false,
			eventIdPrefix,
		);
	}

	async addItems(params: AddItemParams[]) {
		if (!params.length) {
			return null;
		}

		const addedItemData = params.reduce((data, item) => {
			const { bundleId, quantity, comment, index } = item;
			const addedTimestamp = new Date().getTime() + (index ?? 0);
			return {
				...data,
				[bundleId]: {
					bundleId,
					quantity,
					comment,
					addedTimestamp,
					addedBy: CustomerCartStoreImpl.getUserId(),
				},
			};
		}, {});
		const previousQuantities = this.getCart()?.items ?? {};
		params.forEach(({ bundleId, articleData }) => {
			if (articleData) {
				this._optimisticArticles.set(bundleId, articleData);
			}
		});
		return this._eventSourcedCartStore.send(
			new ItemBatchAddedPayload({
				addedItemData,
			}),
			(sessionContext, event) => {
				const response = CustomerCartServerRequests.postItemBatch({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
					items: params.reduce(
						(data, item) => ({
							...data,
							[item.bundleId]: {
								...item,
								quantity: floorToThreeDecimalPlaces(item.quantity),
								sortingIndex: item.index,
							},
						}),
						{},
					),
				});
				params.forEach(({ bundleId, quantity }) =>
					emitChangeItemQuantityCommandEvent({
						cartId: event.cartId,
						commonStatus: "ACTIVE" as CommonStatus, // can only be active
						requestId: event.eventId,
						bundleId,
						previousQuantity: previousQuantities[bundleId]?.quantity ?? 0,
						newQuantity: quantity,
					}),
				);
				return response;
			},
		);
	}

	async restockCart(sourceCartId: string) {
		return this._eventSourcedCartStore.send(
			new CartRestockedPayload({
				addedItemData: {},
			}),
			(sessionContext, event) =>
				CustomerCartServerRequests.postRestockCart({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
					items: {},
					sourceCartId,
				}),
			true,
		);
	}

	async setItemQuantity(bundleId: string, quantity: number, eventIdPrefix?: string) {
		const cart = this._eventSourcedCartStore.getOptimisticState();

		if (cart && !cart.items[bundleId]) {
			return this.addItem(
				{
					bundleId,
					quantity,
				},
				eventIdPrefix,
			);
		}

		if (quantity < minQuantity) {
			return this.removeItem(bundleId, eventIdPrefix);
		}
		const previousQuantity = cart?.items[bundleId]?.quantity ?? 0;

		const scaledQuantity = floorToThreeDecimalPlaces(quantity);
		return this._eventSourcedCartStore.send(
			new ItemQuantitySetPayload({
				bundleId,
				quantity,
				addedTimestamp: new Date().getTime(),
				addedBy: CustomerCartStoreImpl.getUserId(),
			}),
			(sessionContext, event) => {
				const response = CustomerCartServerRequests.putItemQuantity({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
					bundleId,
					quantity: scaledQuantity,
				});
				emitChangeItemQuantityCommandEvent({
					cartId: event.cartId,
					commonStatus: "ACTIVE" as CommonStatus, // can only be active
					requestId: event.eventId,
					bundleId,
					previousQuantity,
					newQuantity: quantity,
				});
				return response;
			},
			false,
			eventIdPrefix,
		);
	}

	async setItemServiceOption(bundleId: string, serviceOptionId: string, selected: boolean) {
		const cart = this.getCart();
		const itemToChange = cart && cart.items[bundleId];
		const serviceOption =
			itemToChange && itemToChange.serviceOptions?.find((option) => option.id === serviceOptionId);

		if (!itemToChange || (serviceOption && serviceOption.selected === selected)) {
			return null;
		}

		const serviceOptionIds = setServiceOption(serviceOptionId, selected, itemToChange.serviceOptions!)
			.filter((option) => option.selected)
			.map((option) => option.id);
		return this._eventSourcedCartStore.send(
			new ItemServiceOptionsSetPayload({
				bundleId,
				serviceOptionIds,
			}),
			(sessionContext, event) => {
				const response = CustomerCartServerRequests.putItemServiceOption({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
					bundleId,
					serviceOptionIds,
				});
				return response;
			},
		);
	}

	async setItemComment(bundleId: string, comment: string) {
		return this._eventSourcedCartStore.send(
			new ItemCommentSetPayload({
				bundleId,
				comment,
			}),
			(sessionContext, event) => {
				const response = CustomerCartServerRequests.putItemComment({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
					bundleId,
					comment,
				});
				return response;
			},
		);
	}

	async setManualPrice(bundleId: string, newPrice: number, reset: boolean, isBbd: boolean) {
		return this._eventSourcedCartStore.send(
			new SetManualPricePayload({
				bundleId,
				newPrice,
				reset,
				isBbd,
				approved: false,
			}),
			(sessionContext, event) => {
				const response = CustomerCartServerRequests.setManualPrice({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
					bundleId,
					newPrice,
					reset,
					isBbd,
				});
				return response;
			},
		);
	}

	async saveCart(cartName: string) {
		const result = await this._eventSourcedCartStore.send(
			new CartSavedPayload({
				cartName,
				clearCart: true,
			}),
			(sessionContext, event) =>
				CustomerCartServerRequests.saveCart({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
					cartName,
					clearCart: true,
				}),
			true,
		);
		SavedCartActions.addSavedCartOptimistically({ ...this._eventSourcedCartStore.getOptimisticState(), cartName });
		return result;
	}

	async setDeliveryDate(date: Date) {
		return this._eventSourcedCartStore.send(
			new DeliveryDateSetPayload({
				deliveryDate: formatLocalDate(date, DELIVERY_DATE_FORMAT),
			}),
			(sessionContext, event) => {
				const response = CustomerCartServerRequests.setDeliveryDate({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
					deliveryDate: event.payload.data.deliveryDate,
				});
				return response;
			},
		);
	}

	async setOrderOptions(orderOptions: OrderOptions) {
		return this._eventSourcedCartStore.send(new OrderHeaderChangedPayload(), (sessionContext, event) =>
			CustomerCartServerRequests.setOrderOptions({
				sessionContext,
				requestId: event.eventId,
				cartId: event.cartId,
				...orderOptions,
			}),
		);
	}

	async setPaymentOptions(paymentType: PaymentType) {
		return this._eventSourcedCartStore.send(new OrderHeaderChangedPayload(), (sessionContext, event) =>
			CustomerCartServerRequests.setPaymentOptions({
				sessionContext,
				requestId: event.eventId,
				cartId: event.cartId,
				paymentType: paymentType.paymentType,
				paymentAllowance: paymentType.paymentAllowance,
			}),
		);
	}

	async setOrderSource(orderSource: OrderSource | null | undefined) {
		return this._eventSourcedCartStore.send(new OrderHeaderChangedPayload(), (sessionContext, event) =>
			CustomerCartServerRequests.setOrderSource({
				sessionContext,
				requestId: event.eventId,
				cartId: event.cartId,
				orderSource: orderSource != null ? orderSource.name : null,
			}),
		);
	}

	async setOrderComments(orderComments: OrderComments) {
		return this._eventSourcedCartStore.send(new OrderHeaderChangedPayload(), (sessionContext, event) =>
			CustomerCartServerRequests.setOrderComments({
				sessionContext,
				requestId: event.eventId,
				cartId: event.cartId,
				...orderComments,
			}),
		);
	}

	async setOwnOrderNumber(ownOrderNumber: string) {
		return this._eventSourcedCartStore.send(new OrderHeaderChangedPayload(), (sessionContext, event) =>
			CustomerCartServerRequests.setOwnOrderNumber({
				sessionContext,
				requestId: event.eventId,
				cartId: event.cartId,
				ownOrderNumber: ownOrderNumber,
			}),
		);
	}

	async setOwnStoreNumber(ownStoreNumber: string) {
		const payload = new OrderHeaderChangedPayload();
		return this._eventSourcedCartStore.send(payload, (sessionContext, event) =>
			CustomerCartServerRequests.setOwnStoreNumber({
				sessionContext,
				requestId: event.eventId,
				cartId: event.cartId,
				ownStoreNumber: ownStoreNumber,
			}),
		);
	}

	async checkOut(
		cartId: string | null | undefined,
		cartVersion: number | null | undefined,
		employeeOrderHeader: EmployeeOrderHeader | null | undefined,
	): Promise<string | null | undefined> {
		// #SDCORP-20649: When we don't reset the checkout state, we will predict the
		// next checkout step based on potentially outdated state in the aggregator store.
		//
		// It is not (trivially) possible to solve this at a different place due to the
		// races that happen during the redirection phase.
		this._checkoutAggregatorStore.resetState();

		const cart = this._eventSourcedCartStore.getOptimisticState();
		const fsdDeliveryDate = cart?.fsdDeliveryDate;
		const shownDeliveryDate = isFsdDeliveryDateBeforeCheckoutEnabled() ? fsdDeliveryDate : null;

		const response = await this._eventSourcedCartStore.send(
			new CheckedOutPayload({
				checkoutId: null,
				removedBundleIds: [],
			}),
			(sessionContext, event) =>
				CustomerCartServerRequests.createCheckout({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
					...this.getEmployeeCheckoutParams(employeeOrderHeader),
					shownDeliveryDate: shownDeliveryDate,
				}),
			true,
		);
		return response ? response.checkoutId : null;
	}

	async removeItem(bundleId: string, eventIdPrefix?: string) {
		const oldCart = this.getCart();
		if (!oldCart) return;

		const trackingContext = {
			country: oldCart.country,
			storeId: oldCart.storeId,
			cartId: oldCart.cartId,
		};
		TrackingStoreGAUtils.trackGAItemRemoved(bundleId, oldCart, trackingContext);
		const previousQuantity = oldCart.items[bundleId]?.quantity ?? 0;

		return this._eventSourcedCartStore.send(
			new ItemRemovedPayload({
				bundleId,
			}),
			(sessionContext, event) => {
				const response = CustomerCartServerRequests.deleteItem({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
					bundleId,
				});
				emitChangeItemQuantityCommandEvent({
					cartId: event.cartId,
					commonStatus: "ACTIVE" as CommonStatus, // can only be active
					requestId: event.eventId,
					bundleId,
					previousQuantity,
					newQuantity: 0,
				});
				return response;
			},
			false,
			eventIdPrefix,
		);
	}

	async removeItems(bundleIds: string[]) {
		bundleIds.forEach(async (bundleId) => this.removeItem(bundleId));
	}

	async loadSavedCart(savedCartId: string) {
		const payload = new SavedCartLoadedPayload({
			addedItemData: {},
		});
		return this._eventSourcedCartStore.send(
			payload,
			(sessionContext, event) =>
				CustomerCartServerRequests.postLoadSavedCart({
					sessionContext,
					requestId: event.eventId,
					cartId: event.cartId,
					items: {},
					sourceCartId: savedCartId,
				}),
			true,
		);
	}

	getEmployeeCheckoutParams(employeeOrderHeader: EmployeeOrderHeader | null | undefined) {
		const { orderData } = this._eventSourcedCartStore.getOptimisticState() || {};
		const employeeOrderData =
			SessionStore.isEmployee() &&
			employeeOrderHeader &&
			CustomerCartStoreImpl.getEmployeeOrderData(employeeOrderHeader, orderData);
		return employeeOrderData
			? {
					employeeOrderData,
				}
			: {};
	}

	static getEmployeeOrderData(orderHeader: EmployeeOrderHeader, cartOrderData: OrderData | null | undefined) {
		const { deliveryDate } = cartOrderData || {};
		const {
			realPaymentType,
			orderSource,
			shoppingCartComment,
			sendOrderConfirmationToCustomer: sendConfirmationToCustomer,
			sendOrderConfirmationToCardholder: sendConfirmationToCardholder,
			singleOrder,
			ignoreFees: ignoreMinOrderValue,
			sendPickingListToCentralWarehouse: externalCommissioning,
			applyEgalisationTax,
			customerDataAdditions: { pickingComment: picking, deliveryComment: delivery, invoiceComment: invoice },
			ownOrderNumber,
			ownStoreNumber,
		} = orderHeader;
		const { paymentType, paymentAllowance } = realPaymentType || {};
		return {
			deliveryDate,
			orderComments: {
				general: shoppingCartComment || "",
				picking,
				delivery,
				invoice,
			},
			payment: {
				paymentType,
				paymentAllowance,
			},
			orderSource: orderSource ? orderSource.name : null,
			orderOptions: {
				sendConfirmationToCustomer,
				sendConfirmationToCardholder,
				externalCommissioning,
				ignoreMinOrderValue,
				singleOrder,
				applyEgalisationTax,
			},
			ownNumbers: {
				ownOrderNumber,
				ownStoreNumber,
			},
		};
	}

	async _mergeAnonCart(anonCart: AnonCart, targetCartId: string) {
		const bundleIds = Object.keys(anonCart.items);

		if (bundleIds.length === 0) {
			return;
		}

		const params: AddItemParams[] = bundleIds.map((bundleId, idx) => ({
			bundleId,
			quantity: anonCart.items[bundleId].quantity,
			comment: "",
			index: idx,
		}));

		try {
			await this.addItems(params);
			AnonCartActions.markCartMerged(anonCart, targetCartId);
		} catch (e) {
			console.error("Could not merge anon cart: ", e);
			// We have to notify the anon cart that the merge failed,
			// so it stops being active.
			AnonCartActions.onMergeFailed();
		}
	}

	static getUserId(): string {
		return SessionStore.isEmployee() ? SessionStore.getEmployeeId() : SessionStore.getCustomerId();
	}
}
export type OrderOptions = {
	sendConfirmationToCustomer?: boolean | null;
	sendConfirmationToCardholder?: boolean | null;
	externalCommissioning?: boolean | null;
	ignoreMinOrderValue?: boolean | null;
	singleOrder?: boolean | null;
	applyEgalisationTax?: boolean | null;
};
export type OrderComments = {
	general?: string | null;
	picking?: string | null;
	delivery?: string | null;
	invoice?: string | null;
};
export const CustomerCartStore = new CustomerCartStoreImpl(
	new EventSourcedCartStoreImpl(
		"CustomerCart",
		"ACTIVE",
		CustomerCartContextStore,
		CustomerDataStore,
		CustomerCartEventRepository,
		CustomerCartWebsocketConnector,
		new SequentialRequestProcessor(),
	),
	CheckoutAggregatorStore,
	SavedCartsStore,
	DeliveryDatesStore,
);
